import { useState, useEffect, useRef, useCallback } from "react";
import { getContacts } from "../services/crmContacts";
import "./TeamTypeahead.css";

export interface CoachOption {
  id: string;
  displayName: string;
}

interface CoachTypeaheadProps {
  value: CoachOption | null;
  onSelect: (coach: CoachOption | null) => void;
  placeholder?: string;
  error?: string;  /**
   * Set so a caller's `<label htmlFor>` can name the search field. Applies while the
   * search box is showing — once a value is selected the box is replaced by a
   * read-only summary, so the id (and the label it anchors) goes with it.
   */
  inputId?: string;

}

export const CoachTypeahead = ({
  inputId,
  value,
  onSelect,
  placeholder = "Search coaches...",
  error,
}: CoachTypeaheadProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<CoachOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!searchTerm.trim() || !isOpen) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await getContacts({ contactType: "Coach", search: searchTerm, pageSize: 20 });
        setResults(data.items.map((c) => ({ id: c.id, displayName: c.displayName })));
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (coach: CoachOption) => {
      onSelect(coach);
      setSearchTerm("");
      setIsOpen(false);
      setResults([]);
    },
    [onSelect]
  );

  const handleClear = () => {
    onSelect(null);
    setSearchTerm("");
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div className="team-typeahead" ref={wrapperRef}>
      <div className={`team-typeahead__input-wrapper ${error ? "team-typeahead__input-wrapper--error" : ""}`}>
        {value ? (
          <div className="team-typeahead__selected">
            <span className="team-typeahead__selected-name">{value.displayName}</span>
            <svg className="team-typeahead__checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <button type="button" className="team-typeahead__clear" onClick={handleClear} aria-label="Clear selection">
              ×
            </button>
          </div>
        ) : (
          <input
          id={inputId}
            ref={inputRef}
            type="text"
            className="team-typeahead__input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
          />
        )}
      </div>

      {error && <div className="team-typeahead__error">{error}</div>}

      {isOpen && !value && (
        <div className="team-typeahead__dropdown">
          {isLoading ? (
            <div className="team-typeahead__loading">
              <div className="team-typeahead__spinner" />
              <span>Searching...</span>
            </div>
          ) : results.length > 0 ? (
            results.map((c) => (
              <button key={c.id} type="button" className="team-typeahead__option" onClick={() => handleSelect(c)}>
                {c.displayName}
              </button>
            ))
          ) : searchTerm.trim() ? (
            <div className="team-typeahead__empty">No coaches found</div>
          ) : (
            <div className="team-typeahead__empty">Start typing to search coaches</div>
          )}
        </div>
      )}
    </div>
  );
};
